/**
 * Centralized configuration for delivery and payment processing
 * This file contains all hardcoded values related to delivery functionality
 */

export const DELIVERY_CONFIG = {
  // Processing fees
  fees: {
    SELLER_FEE_PERCENT: 0.1,   // 10% seller processing fee
    BUYER_FEE_PERCENT: 0.15,   // 15% buyer processing fee
    TEST_PAYMENT_AMOUNT: 0.5,  // $0.50 for testing
  },

  // Timing configurations (in milliseconds)
  timing: {
    PICKUP_READY_OFFSET_MS: 0,           // Ready immediately
    PICKUP_DEADLINE_OFFSET_MS: 1800000,  // 30 minutes from now
    DROPOFF_READY_OFFSET_MS: 0,          // Ready immediately  
    DROPOFF_DEADLINE_OFFSET_MS: 7200000, // 2 hours from now
    
    // Test delivery timing (longer windows for testing)
    TEST_PICKUP_READY_OFFSET_MS: 10000,      // 10 seconds from now
    TEST_PICKUP_DEADLINE_OFFSET_MS: 1000000,  // ~16.7 minutes from now
    TEST_DROPOFF_READY_OFFSET_MS: 600000,     // 10 minutes from now
    TEST_DROPOFF_DEADLINE_OFFSET_MS: 1900000, // ~31.7 minutes from now
    
    // Quote expiration
    QUOTE_EXPIRATION_OFFSET_MS: 900000,  // 15 minutes from now
    
    // Default ETA fallbacks
    DEFAULT_DROPOFF_ETA_OFFSET_MS: 3600000, // 1 hour from now
    DEFAULT_DROPOFF_DEADLINE_OFFSET_MS: 7200000, // 2 hours from now
  },

  // Package specifications
  package: {
    DEFAULT_VALUE_CENTS: 1000,  // $10.00 default value
  },

  // Geographical calculations
  geo: {
    EARTH_RADIUS_MILES: 3958.8,  // Earth's radius in miles (standardized)
  },

  // Currency conversion
  currency: {
    CENTS_TO_DOLLARS_DIVISOR: 100,
  },

  // Uber API configuration
  uber: {
    AUTH_SCOPE: 'eats.deliveries',
    CLIENT_HEADER: 'jadetrade_app',
  },

  // Address validation
  address: {
    REQUIRED_FIELDS: ['street_line1', 'city', 'state', 'zip_code'],
    DEFAULT_COUNTRY: 'US',
  },
} as const;

// Helper functions for common operations
export const DeliveryHelpers = {
  /**
   * Convert cents to dollars with proper formatting
   */
  centsToeDollars: (cents: number): number => {
    return cents / DELIVERY_CONFIG.currency.CENTS_TO_DOLLARS_DIVISOR;
  },

  /**
   * Convert dollars to cents
   */
  dollarsToCents: (dollars: number): number => {
    return Math.round(dollars * DELIVERY_CONFIG.currency.CENTS_TO_DOLLARS_DIVISOR);
  },

  /**
   * Calculate seller fee
   */
  calculateSellerFee: (subtotal: number): number => {
    return Number((subtotal * DELIVERY_CONFIG.fees.SELLER_FEE_PERCENT).toFixed(2));
  },

  /**
   * Calculate buyer fee (15% of order + delivery)
   */
  calculateBuyerFee: (subtotal: number, deliveryFee: number = 0): number => {
    const orderPlusDelivery = subtotal + deliveryFee;
    return Number((orderPlusDelivery * DELIVERY_CONFIG.fees.BUYER_FEE_PERCENT).toFixed(2));
  },

  /**
   * Calculate total processing fee
   */
  calculateTotalProcessingFee: (subtotal: number, deliveryFee: number = 0): number => {
    return DeliveryHelpers.calculateSellerFee(subtotal) + DeliveryHelpers.calculateBuyerFee(subtotal, deliveryFee);
  },

  /**
   * Create pickup deadline timestamp
   */
  createPickupDeadline: (isTest: boolean = false): string => {
    const offset = isTest 
      ? DELIVERY_CONFIG.timing.TEST_PICKUP_DEADLINE_OFFSET_MS
      : DELIVERY_CONFIG.timing.PICKUP_DEADLINE_OFFSET_MS;
    return new Date(Date.now() + offset).toISOString();
  },

  /**
   * Create dropoff deadline timestamp
   */
  createDropoffDeadline: (isTest: boolean = false): string => {
    const offset = isTest 
      ? DELIVERY_CONFIG.timing.TEST_DROPOFF_DEADLINE_OFFSET_MS
      : DELIVERY_CONFIG.timing.DROPOFF_DEADLINE_OFFSET_MS;
    return new Date(Date.now() + offset).toISOString();
  },

  /**
   * Create pickup ready timestamp
   */
  createPickupReady: (isTest: boolean = false): string => {
    const offset = isTest 
      ? DELIVERY_CONFIG.timing.TEST_PICKUP_READY_OFFSET_MS
      : DELIVERY_CONFIG.timing.PICKUP_READY_OFFSET_MS;
    return new Date(Date.now() + offset).toISOString();
  },

  /**
   * Create dropoff ready timestamp
   */
  createDropoffReady: (isTest: boolean = false): string => {
    const offset = isTest 
      ? DELIVERY_CONFIG.timing.TEST_DROPOFF_READY_OFFSET_MS
      : DELIVERY_CONFIG.timing.DROPOFF_READY_OFFSET_MS;
    return new Date(Date.now() + offset).toISOString();
  },

  /**
   * Create default manifest item (Uber handles size/weight/dimensions automatically)
   */
  createManifestItem: (name: string, quantity: number) => ({
    name,
    quantity,
  }),

  /**
   * Calculate distance between two coordinates in miles
   */
  calculateDistance: (lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const R = DELIVERY_CONFIG.geo.EARTH_RADIUS_MILES;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  },

  /**
   * Validate address has required fields
   */
  validateAddress: (address: any, type: 'vendor' | 'buyer'): void => {
    const requiredFields = DELIVERY_CONFIG.address.REQUIRED_FIELDS;
    const missing = requiredFields.filter(field => {
      // Handle both old and new field names
      if (field === 'zip_code' && address?.zip) {
        return false; // Not missing if we have the old 'zip' field
      }
      return !address?.[field];
    });
    
    if (missing.length > 0) {
      throw new Error(
        `Incomplete ${type} address. Missing: ${missing.join(', ')}`
      );
    }
  },

  /**
   * Create robo courier test specifications for automated delivery testing
   * Per Uber Direct API guide: https://developer.uber.com/docs/deliveries/guides/robocourier
   * 
   * @param mode - 'auto' for automatic progression (~30s intervals) or 'custom' for specific timestamps
   * @param customTimestamps - Optional custom timestamps for 'custom' mode
   * @returns TestSpecifications object for robo courier
   */
  createRoboCourierSpec: (
    mode?: 'auto' | 'custom',
    customTimestamps?: {
      enroute_for_pickup_at?: string;
      pickup_imminent_at?: string;
      pickup_at?: string;
      dropoff_imminent_at?: string;
      dropoff_at?: string;
    }
  ) => {
    const selectedMode = mode || 'auto';
    
    if (selectedMode === 'auto') {
      return {
        test_specifications: {
          robo_courier_specification: {
            mode: 'auto' as const,
          },
        },
      };
    }

    // Custom mode requires all timestamps
    if (!customTimestamps) {
      throw new Error('Custom mode requires customTimestamps parameter');
    }

    return {
      test_specifications: {
        robo_courier_specification: {
          mode: 'custom' as const,
          ...customTimestamps,
        },
      },
    };
  },
};